/**
 * SoilProgramsListPage — the Soil workspace's "Program setup" tab (U4).
 *
 * Two independently-actioned FormSection cards over DataTables, replacing the
 * raw <table class="ntr-matrix"> this tab used before. FormSection has NO
 * actions slot (id/title/description/children only), so each card's action
 * button lives in children, above its table.
 */
import React, { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DataTable, FormSection, Modal, Notice, StatusPill,
} from '../patterns';
import type { DataTableColumn } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { SoilWorkspaceContext } from './SoilWorkspaceLayout';
import { soilProgramService, SoilProgram } from '../../services/soilProgramService';
import { soilGroupSetService, SoilGroupSet } from '../../services/soilGroupSetService';
import { naturalCompare } from '../../utils/naturalCompare';
import { formatDate } from '../../dateFormat';
import './nutrition.css';

const issueCount = (p: SoilProgram): number => {
  const v = p.validation;
  if (!v) return 0;
  return v.unknownProducts.length + v.unknownOrchards.length
    + v.duplicateOrchardRows.length + v.duplicateColumns.length;
};

const statusVariant = (status: SoilProgram['status']): 'ok' | 'warn' | 'info' =>
  (status === 'active' ? 'ok' : status === 'received' ? 'warn' : 'info');

const SoilProgramsListPage: React.FC = () => {
  const { t } = useTranslation();
  const [programs, setPrograms] = useState<SoilProgram[]>([]);
  const [groupSets, setGroupSets] = useState<SoilGroupSet[]>([]);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<SoilProgram | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteSetTarget, setDeleteSetTarget] = useState<SoilGroupSet | null>(null);
  const [deleteSetBusy, setDeleteSetBusy] = useState(false);

  // The setup tab is a workspace child, so the layout hands it the resolved
  // programme. Null-safe: useOutletContext returns null outside an <Outlet>
  // (the standalone test suite renders this page bare inside a MemoryRouter).
  const workspace = useOutletContext<SoilWorkspaceContext | null>();
  const programId = workspace?.program?._id;

  // Hooks must sit above every early return — a hook below a guard changes
  // hook order between renders and React throws.
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    // Carry ?program= or the layout falls back to the tenant's ACTIVE
    // programme and the crumb returns the user to a different board (#429).
    { label: t('nutrition.soil.workspace.title'), to: programId ? `/nutrition/soil?program=${programId}` : '/nutrition/soil' },
    { label: t('nutrition.soil.workspace.tab.setup') },
  ]);

  const reloadPrograms = () => soilProgramService.list()
    .then(setPrograms)
    .catch(() => setError(t('nutrition.soil.setup.loadFailed')));

  const reloadGroupSets = () => soilGroupSetService.list()
    .then(setGroupSets)
    .catch(() => setError(t('nutrition.soil.setup.loadFailed')));

  useEffect(() => {
    // Independent catches: one failed fetch must not blank the other card.
    reloadPrograms();
    reloadGroupSets();
    // `t` is deliberately absent: react-i18next's t is not referentially
    // stable and would re-fire this effect on every render (the S2
    // month-picker trap).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    setError('');
    setDeleteBusy(true);
    try {
      await soilProgramService.remove(deleteTarget._id);
      setDeleteTarget(null);
      await reloadPrograms();
    } catch (err) {
      // Branch on the CODE, never the message — the server sentence is prose.
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      setError(code === 'PROGRAM_ACTIVE'
        ? t('nutrition.soil.setup.programs.deleteBlockedActive')
        : t('nutrition.soil.setup.programs.deleteFailed'));
      setDeleteTarget(null);
    } finally {
      setDeleteBusy(false);
    }
  };

  const onConfirmDeleteSet = async () => {
    if (!deleteSetTarget) return;
    setError('');
    setDeleteSetBusy(true);
    try {
      // No confirm flag and no body: DELETE /soil-group-sets/:id takes neither
      // (routes/nutrition.js). It is a SOFT delete, and listGroupSets filters
      // deletedAt: null, so the reload simply drops the row.
      await soilGroupSetService.remove(deleteSetTarget._id);
      setDeleteSetTarget(null);
      await reloadGroupSets();
    } catch (_err) {
      setError(t('nutrition.soil.setup.groupSets.deleteFailed'));
      setDeleteSetTarget(null);
    } finally {
      setDeleteSetBusy(false);
    }
  };

  const programColumns: DataTableColumn<SoilProgram>[] = [
    { key: 'season', label: t('nutrition.soil.setup.programs.col.season'), render: (p) => p.season },
    {
      key: 'source',
      label: t('nutrition.soil.setup.programs.col.source'),
      render: (p) => p.source?.vendor || p.source?.filename || '—',
    },
    {
      key: 'orchards',
      label: t('nutrition.soil.setup.programs.col.orchards'),
      render: (p) => Array.from(new Set(p.entries.map((e) => e.orchardName))).length,
    },
    {
      key: 'products',
      label: t('nutrition.soil.setup.programs.col.products'),
      render: (p) => Array.from(new Set(p.entries.map((e) => e.productName))).length,
    },
    {
      key: 'status',
      label: t('nutrition.soil.setup.programs.col.status'),
      render: (p) => {
        const issues = issueCount(p);
        return (
          <span className="ntr-setup__status">
            <StatusPill variant={statusVariant(p.status)}>
              {t(`nutrition.soil.programs.statuses.${p.status}`, p.status)}
            </StatusPill>
            {issues > 0 && (
              <span className="ntr-setup__issues" data-testid={`program-issues-${p._id}`}>
                {t('nutrition.soil.setup.programs.issues', { count: issues })}
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'action',
      label: '',
      render: (p) => (
        <span className="ntr-setup__actions">
          <Link
            className="pat-btn"
            data-testid={`program-action-${p._id}`}
            to={p.status === 'active' ? `/nutrition/soil?program=${p._id}` : `/nutrition/soil/programs/${p._id}/review`}
          >
            {t('nutrition.soil.setup.programs.open')}
          </Link>
          {/* Active programs are undeletable server-side (sessions reference
              them) — the button never shows for one, so the setup tab never
              offers an affordance that would 409 every time. */}
          {p.status !== 'active' && (
            <button
              type="button"
              className="pat-btn pat-btn--danger"
              data-testid={`program-delete-${p._id}`}
              onClick={() => setDeleteTarget(p)}
            >
              {t('nutrition.soil.setup.programs.delete')}
            </button>
          )}
        </span>
      ),
    },
  ];

  const groupSetColumns: DataTableColumn<SoilGroupSet>[] = [
    { key: 'name', label: t('nutrition.soil.setup.groupSets.col.name'), render: (s) => s.name },
    {
      key: 'groups',
      label: t('nutrition.soil.setup.groupSets.col.groups'),
      render: (s) => <span data-testid={`gs-groups-${s._id}`}>{s.groups.length}</span>,
    },
    {
      key: 'orchards',
      label: t('nutrition.soil.setup.groupSets.col.orchards'),
      render: (s) => (
        <span data-testid={`gs-orchards-${s._id}`}>
          {Array.from(new Set(s.groups.flatMap((g) => g.orchardNames))).length}
        </span>
      ),
    },
    {
      key: 'operators',
      label: t('nutrition.soil.setup.groupSets.col.operators'),
      render: (s) => {
        const names = Array.from(new Set(s.groups.flatMap((g) => g.defaultOperators.map((o) => o.name))));
        return <span data-testid={`gs-operators-${s._id}`}>{names.length ? names.join(', ') : '—'}</span>;
      },
    },
    {
      key: 'lastUsed',
      label: t('nutrition.soil.setup.groupSets.col.lastUsed'),
      render: (s) => (s.lastUsedAt ? formatDate(s.lastUsedAt) : '—'),
    },
    {
      key: 'action',
      label: '',
      render: (s) => {
        // The editor at /nutrition/soil/group-sets/:id is a SIBLING of the
        // workspace, not a tab, and its :id is a *set* id — there is no
        // programme to reconstruct from it. The only way its crumbs can
        // return the user to the board they came from is if this link hands
        // the programme over explicitly.
        const to = `/nutrition/soil/group-sets/${s._id}${programId ? `?program=${programId}` : ''}`;
        return (
          <span className="ntr-setup__actions">
            <Link className="pat-btn" data-testid={`gs-action-${s._id}`} to={to}>
              {t('nutrition.soil.setup.groupSets.open')}
            </Link>
            {/* Unlike a program, a set has no status gate: the route deletes
                unconditionally and a set is a template snapshot, so no session
                depends on it. Every row is deletable. */}
            <button
              type="button"
              className="pat-btn pat-btn--danger"
              data-testid={`gs-delete-${s._id}`}
              onClick={() => setDeleteSetTarget(s)}
            >
              {t('nutrition.soil.setup.groupSets.delete')}
            </button>
          </span>
        );
      },
    },
  ];

  return (
    <div className="pat-page">
      {error && <Notice tone="danger" testId="soil-setup-error">{error}</Notice>}

      <FormSection id="soil-setup-programs" title={t('nutrition.soil.setup.programs.title')}>
        <div className="ntr-setup__bar">
          <Link className="pat-btn pat-btn--primary" data-testid="soil-import-btn" to="/nutrition/soil/setup/import">
            {t('nutrition.soil.setup.programs.import')}
          </Link>
        </div>
        <DataTable<SoilProgram>
          columns={programColumns}
          rows={[...programs].sort((a, b) => naturalCompare(b.season, a.season))}
          rowKey={(p) => p._id}
          emptyState={<div data-testid="programs-empty">{t('nutrition.soil.setup.programs.empty')}</div>}
          mobileCardRow={(p) => {
            const orchardCount = Array.from(new Set(p.entries.map((e) => e.orchardName))).length;
            const productCount = Array.from(new Set(p.entries.map((e) => e.productName))).length;
            return (
              <div>
                <strong>{p.season} · {p.source?.vendor || p.source?.filename || '—'}</strong>
                <div style={{ fontSize: 11, color: 'var(--shell-text-muted)' }}>
                  {orchardCount} {t('nutrition.soil.setup.programs.col.orchards')} · {productCount} {t('nutrition.soil.setup.programs.col.products')}
                </div>
              </div>
            );
          }}
        />
      </FormSection>

      <FormSection id="soil-setup-group-sets" title={t('nutrition.soil.setup.groupSets.title')}>
        <div className="ntr-setup__bar">
          <Link
            className="pat-btn pat-btn--primary"
            data-testid="gs-new-btn"
            to={`/nutrition/soil/group-sets/new${programId ? `?program=${programId}` : ''}`}
          >
            {t('nutrition.soil.setup.groupSets.new')}
          </Link>
        </div>
        <DataTable<SoilGroupSet>
          columns={groupSetColumns}
          rows={groupSets}
          rowKey={(s) => s._id}
          emptyState={<div data-testid="gs-empty">{t('nutrition.soil.setup.groupSets.empty')}</div>}
          mobileCardRow={(s) => {
            const orchardCount = Array.from(new Set(s.groups.flatMap((g) => g.orchardNames))).length;
            return (
              <div>
                {/* Combined with the group count so this never renders the bare
                    name text a row's own cell already shows — jsdom has no
                    breakpoint, so both the table row and this card render at
                    once, and a query for the name alone must stay unique. */}
                <strong>{s.name} · {s.groups.length} {t('nutrition.soil.setup.groupSets.col.groups')}</strong>
                <div style={{ fontSize: 11, color: 'var(--shell-text-muted)' }}>
                  {orchardCount} {t('nutrition.soil.setup.groupSets.col.orchards')}
                </div>
              </div>
            );
          }}
        />
      </FormSection>

      <Modal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        title={t('nutrition.soil.setup.programs.deleteConfirmTitle', { season: deleteTarget?.season })}
      >
        <Modal.Body>{t('nutrition.soil.review.deleteConfirmBody')}</Modal.Body>
        <Modal.Footer>
          <button type="button" className="pat-btn pat-btn--subtle" onClick={() => setDeleteTarget(null)}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="pat-btn pat-btn--danger"
            data-testid="program-delete-confirm"
            disabled={deleteBusy}
            onClick={onConfirmDelete}
          >
            {t('nutrition.soil.setup.programs.delete')}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!deleteSetTarget}
        onHide={() => setDeleteSetTarget(null)}
        title={t('nutrition.soil.setup.groupSets.deleteConfirmTitle', { name: deleteSetTarget?.name })}
      >
        <Modal.Body>{t('nutrition.soil.setup.groupSets.deleteConfirmBody')}</Modal.Body>
        <Modal.Footer>
          <button type="button" className="pat-btn pat-btn--subtle" onClick={() => setDeleteSetTarget(null)}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="pat-btn pat-btn--danger"
            data-testid="gs-delete-confirm"
            disabled={deleteSetBusy}
            onClick={onConfirmDeleteSet}
          >
            {t('nutrition.soil.setup.groupSets.delete')}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SoilProgramsListPage;
